import { useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Form } from "../components/Form";
import { FormActions } from "../components/FormActions";
import { FormGroup } from "../components/FormGroup";
import { FormLabel } from "../components/FormLabel";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { Toggle } from "../components/Toggle";
import { CsvFileUpload } from "./components/productImport/CsvFileUpload";
import { ColumnMapping } from "./components/productImport/ColumnMapping";
import { ImportOptions } from "./components/productImport/ImportOptions";
import { DryRunResults } from "./components/productImport/DryRunResults";
import { ImportProgress } from "./components/productImport/ImportProgress";
import { ImportJobHistory } from "./components/productImport/ImportJobHistory";
import { ImportDropZone } from "./components/productImport/ImportDropZone";
import { analyzeCsvFile, parseCSVHeaders } from "./components/productImport/csvUtils";
import {
  useImportProductsMutation,
  useGetImportJobQuery,
  useGetImportJobsHistoryQuery,
} from "../query/productsQuery";
import {
  DEFAULT_IMPORT_MATCH_FIELDS,
  toImportMatchFieldsPayload,
  type ImportDryRunResults,
  type ImportMatchFields,
} from "../api/productsApi";
import { useGetStoreQuery } from "../query/storesQuery";
import { useGetChainQuery } from "../query/chainsQuery";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useProductImportAccess } from "../hooks/useProductImportAccess";
import { useGetActiveFeaturesQuery } from "../query/billingItemsQuery";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { ProductImportSection } from "./components/productImport/ProductImportSection";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";

const AlertsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const DryRunAlertsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const MapContinueHint = styled.p`
  margin: ${spacing.x3}px 0 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.textMuted};
`;

const AddStockHelp = styled.p`
  margin: ${spacing.x2}px 0 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.textMuted};
`;

const AddStockGroup = styled(FormGroup)`
  margin-bottom: ${spacing.x3}px;
`;

export const ProductImportView = () => {
  const { t } = useTranslation();
  const { scope, storeId, chainId } = useCatalogScope();
  const importAccess = useProductImportAccess(scope);
  const isChain = scope?.type === "chain";
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Record<number, string>>({});
  const [skipFirstRow, setSkipFirstRow] = useState(true);
  const [encoding, setEncoding] = useState("utf-8");
  const [delimiter, setDelimiter] = useState(",");
  const [onlyUpdate, setOnlyUpdate] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  const [dryRunSuccess, setDryRunSuccess] = useState<string | null>(null);
  const [dryRunCompleted, setDryRunCompleted] = useState(false);
  const [dryRunResults, setDryRunResults] = useState<ImportDryRunResults | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [variantGroupColumn, setVariantGroupColumn] = useState("");
  const [firstRowAsVariant, setFirstRowAsVariant] = useState(false);
  const [matchFields, setMatchFields] = useState<ImportMatchFields>(
    DEFAULT_IMPORT_MATCH_FIELDS
  );
  const [addStock, setAddStock] = useState(false);

  const storeQuery = useGetStoreQuery(storeId ?? undefined);
  const chainQuery = useGetChainQuery(chainId ?? undefined);
  const extraProductFields = isChain
    ? chainQuery.data?.extraProductFields ?? null
    : storeQuery.data?.extraProductFields ?? null;
  const { data: activeFeatures } = useGetActiveFeaturesQuery(storeId);
  const hasProductVariants = activeFeatures?.includes(Features.PRODUCT_VARIANTS) ?? false;
  const hasRestaurantVariants = activeFeatures?.includes(Features.RESTAURANT_VARIANTS) ?? false;
  const showVariants = isChain || hasProductVariants || hasRestaurantVariants;
  const defaultFirstRowAsVariant = !hasRestaurantVariants;
  const { mutate: importProducts, isPending: isImportPending } =
    useImportProductsMutation(scope);
  const importJobQuery = useGetImportJobQuery(
    scope,
    importJobId,
    !!importJobId
  );
  const importJobsHistoryQuery = useGetImportJobsHistoryQuery(scope);

  const step1Complete = Boolean(csvFile && csvHeaders.length > 0);
  const step2Complete =
    step1Complete &&
    csvHeaders.some((_, i) => {
      const v = columnMappings[i];
      return typeof v === "string" && v.length > 0;
    });

  const mappedGroupColumnOptions = useMemo(() => {
    const uniqueFields = new Set<string>();
    Object.values(columnMappings).forEach((field) => {
      if (field && field !== "SKIP" && field !== "VARIANT_ID") {
        uniqueFields.add(field);
      }
    });
    return Array.from(uniqueFields);
  }, [columnMappings]);

  const resolvedVariantGroupColumn =
    showVariants && mappedGroupColumnOptions.includes(variantGroupColumn)
      ? variantGroupColumn
      : "";
  const variantImportActive = resolvedVariantGroupColumn.length > 0;

  const resetDryRunState = () => {
    setDryRunCompleted(false);
    setDryRunResults(null);
    setDryRunError(null);
    setDryRunSuccess(null);
  };

  const handleFileChange = (file: File | null) => {

    setParseError(null);
    setError(null);
    setSuccessMessage(null);
    setColumnMappings({});
    setVariantGroupColumn("");
    setFirstRowAsVariant(defaultFirstRowAsVariant);
    resetDryRunState();

    if (!file) {
      setCsvFile(null);
      setCsvHeaders([]);
      return;
    }
    setCsvFile(file);

    analyzeCsvFile(file)
      .then(({ headers, encoding: detectedEncoding, delimiter: detectedDelimiter }) => {
        setEncoding(detectedEncoding);
        setDelimiter(detectedDelimiter);
        setCsvHeaders(headers);
        const initialMappings: Record<number, string> = {};
        headers.forEach((_, index) => {
          initialMappings[index] = "";
        });
        setColumnMappings(initialMappings);
      })
      .catch((err: Error) => {
        setParseError(t("productImport.parseCsvFailed", { message: err.message }));
        setCsvHeaders([]);
      });
  };

  const handleEncodingChange = (newEncoding: string) => {
    setEncoding(newEncoding);
    resetDryRunState();
    if (csvFile) {
      parseCSVHeaders(csvFile, newEncoding, delimiter)
        .then((headers) => {
          setCsvHeaders(headers);
          const initialMappings: Record<number, string> = {};
          headers.forEach((_, index) => {
            initialMappings[index] = columnMappings[index] || "";
          });
          setColumnMappings(initialMappings);
        })
        .catch((err: Error) => {
          setParseError(t("productImport.parseCsvFailed", { message: err.message }));
        });
    }
  };

  const handleDelimiterChange = (newDelimiter: string) => {
    setDelimiter(newDelimiter);
    resetDryRunState();
    if (csvFile) {
      parseCSVHeaders(csvFile, encoding, newDelimiter)
        .then((headers) => {
          setCsvHeaders(headers);
          const initialMappings: Record<number, string> = {};
          headers.forEach((_, index) => {
            initialMappings[index] = columnMappings[index] || "";
          });
          setColumnMappings(initialMappings);
        })
        .catch((err: Error) => {
          setParseError(t("productImport.parseCsvFailed", { message: err.message }));
        });
    }
  };

  const handleMappingChange = (index: number, value: string) => {
    setColumnMappings((prev) => ({
      ...prev,
      [index]: value,
    }));
    resetDryRunState();
  };

  const handleVariantGroupColumnChange = (value: string) => {
    setVariantGroupColumn(value);
    resetDryRunState();
  };

  const handleFirstRowAsVariantChange = (value: boolean) => {
    setFirstRowAsVariant(value);
    resetDryRunState();
  };

  const buildImportParams = () => {
    const mappingArray = csvHeaders.map((_, index) => {
      const mapping = columnMappings[index] || "SKIP";
      return mapping === "" ? "SKIP" : mapping;
    });

    const variantGroupColumnParam = variantImportActive
      ? resolvedVariantGroupColumn
      : undefined;

    const importMatchFields = showVariants
      ? matchFields
      : { ...matchFields, variantId: false };

    return {
      columnMapping: mappingArray,
      csvFile: csvFile!,
      defaultValues: {},
      skipFirstRow,
      encoding,
      delimiter,
      onlyUpdate,
      addStock,
      variantGroupColumn: variantGroupColumnParam,
      firstRowAsVariant: variantGroupColumnParam ? firstRowAsVariant : undefined,
      matchFields: toImportMatchFieldsPayload(importMatchFields),
    };
  };

  const validateVariantImportConfig = (): string | null => {
    if (!variantImportActive) {
      return null;
    }
    if (!resolvedVariantGroupColumn) {
      return t("productImport.variantGroupColumnRequired");
    }
    return null;
  };

  const handleDryRun = (e: React.FormEvent) => {
    e.preventDefault();
    setDryRunError(null);
    setDryRunSuccess(null);
    setSuccessMessage(null);
    setDryRunResults(null);

    if (!csvFile) {
      setDryRunError(t("productImport.selectCsvFile"));
      return;
    }

    if (csvHeaders.length === 0) {
      setDryRunError(t("productImport.noCsvHeaders"));
      return;
    }

    const variantValidationError = validateVariantImportConfig();
    if (variantValidationError) {
      setDryRunError(variantValidationError);
      return;
    }

    importProducts(
        {
          ...buildImportParams(),
          dryrun: true,
        },
        {
          onSuccess: (data) => {
            if (!data) {
              setDryRunError(t("productImport.failedRunDryRun"));
              return;
            }
            if ("importJobId" in data) {
              setImportJobId(data.importJobId as string);
              setSuccessMessage(t("productImport.importStartedTracking"));
            } else if ("success" in data) {
              setDryRunResults(data);
              setDryRunCompleted(true);
              setDryRunSuccess(t("productImport.dryRunCompletedReview"));
            }
          },
          onError: (err: Error) => {
            setDryRunError(err.message || t("productImport.dryRunFailed"));
            setDryRunCompleted(false);
            setDryRunResults(null);
          },
        }
      );
  };

  const handleImport = () => {
    setError(null);
    setSuccessMessage(null);
    setIsImporting(true);

    if (!csvFile) {
      setError(t("productImport.selectCsvFile"));
      setIsImporting(false);
      return;
    }

    if (csvHeaders.length === 0) {
      setError(t("productImport.noCsvHeaders"));
      setIsImporting(false);
      return;
    }

    if (!dryRunCompleted) {
      setError(t("productImport.runDryRunFirst"));
      setIsImporting(false);
      return;
    }

    const variantValidationError = validateVariantImportConfig();
    if (variantValidationError) {
      setError(variantValidationError);
      setIsImporting(false);
      return;
    }

    importProducts(
        {
          ...buildImportParams(),
          dryrun: false,
        },
        {
          onSuccess: (data) => {
            if (data && typeof data === "object" && "importJobId" in data) {
              setImportJobId(data.importJobId as string);
              setSuccessMessage(t("productImport.importStartedTracking"));
            } else {
              setSuccessMessage(t("productImport.productsImportedSuccess"));
              setCsvFile(null);
              setCsvHeaders([]);
              setColumnMappings({});
              setVariantGroupColumn("");
              setFirstRowAsVariant(defaultFirstRowAsVariant);
              resetDryRunState();
            }
            setIsImporting(false);
          },
          onError: (err: Error) => {
            setError(err.message || t("productImport.importFailed"));
            setIsImporting(false);
          },
        }
      );
  };

  if (importAccess === "unknown") {
    return (
      <PageContent title={t("productImport.title")}>
        <LoadingContainer />
      </PageContent>
    );
  }

  if (importAccess === "locked") {
    return (
      <PageContent title={t("productImport.title")}>
        <InfoBox variant="info">{t("productImport.storeImportLocked")}</InfoBox>
      </PageContent>
    );
  }

  return (
    <PageContent title={t("productImport.title")}>
      <ImportDropZone onFile={(file) => handleFileChange(file)}>
        <Card>
          {importAccess === "chain" && (
            <AlertsStack>
              <InfoBox variant="info">{t("productImport.chainImportInfo")}</InfoBox>
            </AlertsStack>
          )}
          {importAccess === "storeScoped" && (
            <AlertsStack>
              <InfoBox variant="info">{t("productImport.storeImportScopedInfo")}</InfoBox>
            </AlertsStack>
          )}
          {(parseError || error || successMessage) && (
            <AlertsStack>
              {parseError && <InfoBox variant="error">{parseError}</InfoBox>}
              {error && <InfoBox variant="error">{error}</InfoBox>}
              {successMessage && <InfoBox variant="success">{successMessage}</InfoBox>}
            </AlertsStack>
          )}

          <Form onSubmit={(e) => e.preventDefault()}>
            <ProductImportSection
              title={t("productImport.sectionUploadTitle")}
              description={[
                t("productImport.sectionUploadDescription"),
                t("productImport.sectionMatchExplanation"),
              ]}
            >
              <CsvFileUpload file={csvFile} onFileChange={handleFileChange} />
              <ImportOptions
                skipFirstRow={skipFirstRow}
                encoding={encoding}
                delimiter={delimiter}
                onlyUpdate={onlyUpdate}
                onSkipFirstRowChange={(value) => {
                  setSkipFirstRow(value);
                  resetDryRunState();
                }}
                onEncodingChange={handleEncodingChange}
                onDelimiterChange={handleDelimiterChange}
                onOnlyUpdateChange={(value) => {
                  setOnlyUpdate(value);
                  resetDryRunState();
                }}
                matchFields={matchFields}
                onMatchFieldsChange={(fields) => {
                  setMatchFields(fields);
                  resetDryRunState();
                }}
                showVariantMatchFields={showVariants}
              />
            </ProductImportSection>

            {step1Complete && (
              <ProductImportSection
                title={t("productImport.sectionMappingTitle")}
                description={t("productImport.sectionMappingDescription")}
              >
                <ColumnMapping
                  headers={csvHeaders}
                  mappings={columnMappings}
                  onMappingChange={handleMappingChange}
                  extraProductFields={extraProductFields}
                  showVariants={showVariants}
                  variantGroupColumn={resolvedVariantGroupColumn}
                  onVariantGroupColumnChange={handleVariantGroupColumnChange}
                  firstRowAsVariant={firstRowAsVariant}
                  onFirstRowAsVariantChange={handleFirstRowAsVariantChange}
                />
                {!step2Complete && (
                  <MapContinueHint>{t("productImport.mapAtLeastOneHint")}</MapContinueHint>
                )}
              </ProductImportSection>
            )}

            {step2Complete && (
              <ProductImportSection
                title={t("productImport.sectionActionsTitle")}
                description={t("productImport.sectionActionsDescription")}
              >
                <AddStockGroup>
                  <FormLabel>{t("productImport.optionsAddStock")}</FormLabel>
                  <Toggle
                    checked={addStock}
                    onChange={(value) => {
                      setAddStock(value);
                      resetDryRunState();
                    }}
                    label={t("productImport.optionsAddStockToggle")}
                  />
                  <AddStockHelp>{t("productImport.optionsAddStockHelp")}</AddStockHelp>
                </AddStockGroup>
                {(dryRunError || dryRunSuccess) && (
                  <DryRunAlertsStack>
                    {dryRunError && <InfoBox variant="error">{dryRunError}</InfoBox>}
                    {dryRunSuccess && <InfoBox variant="success">{dryRunSuccess}</InfoBox>}
                  </DryRunAlertsStack>
                )}
                <FormActions>
                  <Button type="button" $color="secondary" onClick={() => window.history.back()}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleDryRun}
                    disabled={isImportPending || !csvFile || csvHeaders.length === 0}
                  >
                    {isImportPending ? t("productImport.running") : t("productImport.runDryRun")}
                  </Button>
                </FormActions>
              </ProductImportSection>
            )}
          </Form>

          {step2Complete && dryRunResults !== null && (
            <DryRunResults
              results={dryRunResults}
              onImport={handleImport}
              canImport={dryRunCompleted && dryRunResults.success}
              isImporting={isImporting || isImportPending}
            />
          )}
        </Card>

        {importJobId && (
          <ImportProgress
            job={importJobQuery.data}
            isLoading={importJobQuery.isLoading}
          />
        )}

        <ImportJobHistory
          jobs={importJobsHistoryQuery.data}
          isLoading={importJobsHistoryQuery.isLoading}
        />
      </ImportDropZone>
    </PageContent>
  );
};
